/**
 * RecentlyViewedCustomersStrip — Feb 2026 (Slice 2 · Step 4)
 *
 * Horizontal chip strip that surfaces the last 5 customer profiles the current
 * SA / dispatcher has opened, so re-hopping between two profiles is a
 * one-tap action instead of the search → filter → click dance.
 *
 * Backed by localStorage (per-device, per-user, no backend round-trip).
 * `pushRecentlyViewedCustomer(user)` should be called from any code path that
 * opens a customer drawer — currently wired from `UsersAdminPage.openUser`.
 *
 * Self-hides when the list is empty (typical for a fresh device) and on
 * non-dispatch routes / unauthorized roles.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';

const STORAGE_KEY = 'sa_recently_viewed_customers_v1';
const MAX_ITEMS = 5;
const UPDATED_EVENT = 'recently-viewed-customers-updated';

const ROLE_ACCENT = {
  customer: '#39FF14',
  driver: '#00F0FF',
  dispatcher: '#FF9F0A',
  super_admin: '#B87BFF',
};

function loadList() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
  } catch {
    return [];
  }
}

function saveList(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // storage full / private mode — silently degrade
  }
}

// Public: call from any code path that opens a customer profile.
export function pushRecentlyViewedCustomer(user) {
  if (!user || !user.id) return;
  const entry = {
    id: user.id,
    name: user.name || '',
    email: user.email || '',
    role: user.role || 'customer',
    region: user.region || '',
    ts: Date.now(),
  };
  const current = loadList().filter((u) => u.id !== entry.id);
  const next = [entry, ...current].slice(0, MAX_ITEMS);
  saveList(next);
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: next }));
}

// Public: clear the strip (used by the [x] chip and can be called from tests).
export function clearRecentlyViewedCustomers() {
  saveList([]);
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: [] }));
}

export function RecentlyViewedCustomersStrip({ from = 'operations-home' }) {
  const [items, setItems] = useState(() => loadList());

  const refresh = useCallback(() => setItems(loadList()), []);

  useEffect(() => {
    const onUpdate = (e) => setItems(e.detail || loadList());
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener(UPDATED_EVENT, onUpdate);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(UPDATED_EVENT, onUpdate);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  if (!items.length) return null;

  return (
    <div
      className="border border-[#2A0F3D] bg-[#0C0612] px-3 py-2 flex items-center gap-2"
      data-testid="recently-viewed-strip"
    >
      <span className="flex items-center gap-1.5 shrink-0 text-[#4B6B52] font-mono text-[10px] uppercase tracking-widest">
        <Clock className="w-3 h-3" />
        RECENT
      </span>
      <div className="flex-1 flex items-center gap-1.5 overflow-x-auto scrollbar-none">
        {items.map((u) => {
          const accent = ROLE_ACCENT[u.role] || '#A3C7A8';
          const label = (u.name && u.name.trim()) || u.email || 'Unnamed';
          return (
            <Link
              key={u.id}
              to={`/dispatch/admin/users?openUser=${u.id}&from=${from}`}
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border border-[#2A0F3D] hover:border-[#39FF14] transition-colors active:opacity-70"
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              data-testid={`recently-viewed-chip-${u.id}`}
              title={u.email}
            >
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: accent, boxShadow: `0 0 6px ${accent}` }}
              />
              <span className="font-mono text-[11px] text-[#EAE7E1] truncate max-w-[140px]">
                {label}
              </span>
            </Link>
          );
        })}
      </div>
      <button
        type="button"
        onClick={clearRecentlyViewedCustomers}
        className="shrink-0 p-1 text-[#4B6B52] hover:text-[#FF3B30] transition-colors"
        aria-label="Clear recently viewed"
        data-testid="recently-viewed-clear"
        title="Clear list"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default RecentlyViewedCustomersStrip;
